import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import { useApi } from '../api/hooks'
import { Badge } from './ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { EmptyState } from './ui/empty-state'
import { AppIcon } from './ui/icon'
import { AttachDocumentControl, type AttachDocumentControlLabels } from './documents/AttachDocumentControl'
import type { components } from '../api/schema'
import { SECTION_ICONS } from '../lib/section-icons'

type Document = components['schemas']['Document']
type Report = Document & { uid: string }

interface Props {
  org: string
  contractUid: string
  /** Uids of the assets the contract covers: the "Attach a report" picker is limited to their reports. */
  assetUids: string[]
}

const REPORTS_LIMIT = 500

const VERDICT_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  compliant: 'success',
  warning: 'warning',
  critical: 'destructive',
  unknown: 'secondary',
}

/** A property value is stored as JSON: a bare string for dates and enums. */
function textValue(v: unknown): string | null {
  return typeof v === 'string' && v ? v : null
}

/**
 * The visit reports of a contract: the ones filed on it and the ones attached
 * to one of its interventions (spec 2026-10-11-12). One row per report, newest
 * visit first.
 */
export function ContractReportsTab({ org, contractUid, assetUids }: Props) {
  const { t } = useTranslation('contracts')
  const { request } = useApi()
  const queryClient = useQueryClient()
  const [attachError, setAttachError] = useState('')

  const reportsQuery = $api.useQuery('get', '/api/v1/organizations/{orgCode}/documents', {
    params: {
      path: { orgCode: org },
      query: {
        contract_uid: contractUid,
        via_interventions: true,
        document_type_flag: 'visit_report',
        include: ['property_values'],
        limit: REPORTS_LIMIT,
      },
    },
  })

  const rows = useMemo(() => {
    const docs = (((reportsQuery.data as any)?.data as Report[]) || []).map((d) => {
      const pv = ((d as any).property_values || {}) as Record<string, unknown>
      const date = textValue(pv.visit_date) || textValue(pv.doc_date) || (d.created_at ? String(d.created_at) : '')
      return { doc: d, date, verdict: textValue(pv.compliance_status) }
    })
    return docs.sort((a, b) => b.date.localeCompare(a.date))
  }, [reportsQuery.data])

  const linkedUids = useMemo(() => new Set(rows.map((r) => r.doc.uid)), [rows])

  const labels: AttachDocumentControlLabels = useMemo(() => ({
    attachExisting: t('reports.attach'),
    upload: t('documents.upload'),
    uploading: t('documents.upload'),
    pickDocumentTitle: t('reports.pick_title'),
    searchPlaceholder: t('documents.search_documents'),
    noDocumentsFound: t('reports.no_candidates'),
    alreadyAttached: t('documents.already_attached'),
  }), [t])

  const attach = useCallback(async (documentUid: string) => {
    setAttachError('')
    try {
      await request('/api/v1/organizations/{orgCode}/documents/{documentUid}', 'PATCH', {
        params: { path: { orgCode: org, documentUid } },
        body: { contract_uid: contractUid } as never,
      })
      await queryClient.invalidateQueries({ queryKey: ['get', '/api/v1/organizations/{orgCode}/documents'] })
    } catch {
      setAttachError(t('reports.attach_error'))
    }
  }, [request, org, contractUid, queryClient, t])

  return (
    <Card data-testid="contract-reports-tab">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>{t('reports.title')}</CardTitle>
        <AttachDocumentControl
          org={org}
          linkedUids={linkedUids}
          testidPrefix="contract-reports-attach"
          showUpload={false}
          documentTypeFlag="visit_report"
          assetUids={assetUids}
          labels={labels}
          onUploaded={() => {}}
          onSelect={attach}
          className="flex flex-wrap"
        />
      </CardHeader>
      <CardContent>
        {attachError && (
          <p className="mb-3 text-sm text-destructive" role="alert" data-testid="contract-reports-attach-error">
            {attachError}
          </p>
        )}
        {reportsQuery.isLoading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">…</div>
        ) : rows.length === 0 ? (
          <EmptyState
            variant="inline"
            title={t('reports.empty_title')}
            description={t('reports.empty_description')}
            data-testid="contract-reports-empty"
          />
        ) : (
          <ul className="divide-y divide-border">
            {rows.map(({ doc, date, verdict }) => (
              <li
                key={doc.uid}
                className="flex flex-wrap items-center gap-3 py-2"
                data-testid={`contract-report-row-${doc.uid}`}
              >
                <AppIcon tone="muted" path={SECTION_ICONS.documents} size="sm" className="flex-shrink-0" />
                <Link
                  to="/$org/documents/$docUid"
                  params={{ org, docUid: doc.uid }}
                  className="min-w-0 flex-1 truncate text-sm font-medium hover:underline"
                >
                  {doc.name || doc.file_name}
                </Link>
                <span className="text-sm text-muted-foreground">{date.slice(0, 10)}</span>
                {verdict && (
                  <Badge variant={VERDICT_VARIANT[verdict] ?? 'secondary'}>
                    {t(`reports.verdict_${verdict}` as any, { defaultValue: verdict })}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
